/**
 * Runs one knowledge-base training session on the duplex transport: the
 * socket, the microphone stream, customer playback, and the folded state the
 * page renders. Transcript lines and item cards go into the live-call Redux
 * slices through useTrainingCallFeed, so the page renders them with the
 * live-call components.
 *
 * The server decides everything (which item is current, when it is answered,
 * when silence has gone on too long). This hook only relays: there is no
 * silence gate, end-of-turn timer or watchdog on this side, and there must not
 * be one. The microphone streams continuously, silence included, from the
 * moment the session opens (see playbookLiveAudio).
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { KB_LIVE_COPY, TRAINING_CALL_COPY } from '../constants/training';
import { playbookLiveAudio } from '../services/playbookLiveAudio';
import { KB_LIVE_ERRORS, KbLiveHandlers, kbLiveWebSocketService } from '../services/kbLiveWebSocketService';
import {
  INITIAL_KB_LIVE_STATE,
  KbLiveSessionAction,
  KbLiveSessionState,
  kbLiveSessionReducer,
} from '../utils/kbLiveSessionState';
import type { TrainingCallPhase } from '../components/training/TrainingStatusPanel';
import type { TurnState } from '../utils/turnIndicatorState';
import { FirstCustomerVoice, useFirstCustomerVoice } from './useFirstCustomerVoice';
import { TrainingCallFeed, useTrainingCallFeed } from './useTrainingCallFeed';
import { useTurnIndicator } from './useTurnIndicator';
import { usableSessionId } from '../utils/trainingSessionId';

export type KbLivePhase = TrainingCallPhase;

/** How long "End" waits for the server's `session_completed` before giving up. */
export const END_CONFIRMATION_TIMEOUT_MS = 15000;

const END_UNCONFIRMED_MESSAGE =
  'The session ended, but we could not confirm the final results. Check the admin portal for your results.';


export interface KbLiveSession {
  phase: KbLivePhase;
  state: KbLiveSessionState;
  isEnding: boolean;
  /** The turn line: AI Speaking, Your Turn, Processing, slow (useTurnIndicator). */
  turn: TurnState;
  error: string | null;
  clearError: () => void;
  start: () => Promise<void>;
  end: () => void;
  teardown: () => Promise<void>;
}

function buildHandlers(
  dispatch: (action: KbLiveSessionAction) => void,
  feed: TrainingCallFeed,
  voice: FirstCustomerVoice,
  callbacks: {
    onCompleted: () => void;
    onFailed: (message: string) => void;
    onCustomerAudio: (pcm: ArrayBuffer) => void;
  },
): KbLiveHandlers {
  return {
    onSessionStarted: (data) => dispatch({ type: 'session_started', data }),
    onTranscriptDelta: (data) => {
      feed.appendTranscript(data.speaker, data.text);
      if (data.speaker === 'ai_customer') voice.noteCustomerText(data.text);
      if (data.speaker === 'salesperson' && data.text.trim()) dispatch({ type: 'rep_spoke' });
    },
    onItemRaised: (data) => {
      // The customer's line that raised the item is over.
      feed.flushTranscript();
      dispatch({ type: 'item_raised', data });
    },
    onQuestionDetected: feed.questionDetected,
    onStoredAnswer: feed.storedAnswer,
    onLandmineAlert: feed.landmineAlert,
    onCompetitorAlert: feed.competitorAlert,
    onItemAnswered: (data) => {
      feed.flushTranscript();
      dispatch({ type: 'item_answered', data });
    },
    onSilence: (data) => dispatch({ type: 'silence', data }),
    onSessionCompleted: (data) => {
      feed.flushTranscript();
      dispatch({ type: 'session_completed', data });
      callbacks.onCompleted();
    },
    onCustomerAudio: (pcm) => {
      playbookLiveAudio.playChunk(pcm);
      voice.noteCustomerAudio(pcm);
      callbacks.onCustomerAudio(pcm);
    },
    onError: callbacks.onFailed,
  };
}

export function useKbLiveSession(trackId: string | null, sessionId?: string): KbLiveSession {
  const [phase, setPhase] = useState<KbLivePhase>('preflight');
  const [state, dispatch] = useReducer(kbLiveSessionReducer, INITIAL_KB_LIVE_STATE);
  const [isEnding, setIsEnding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const feed = useTrainingCallFeed();
  const phaseRef = useRef<KbLivePhase>('preflight');
  const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failRef = useRef<(message: string) => void>(() => undefined);
  // Destructured: only the stable callbacks go into dependencies, so a turn
  // change never re-creates teardown (whose unmount effect would then run).
  const { turn, noteCustomerAudio, noteMicAudio, reset: resetTurn } = useTurnIndicator();

  const moveTo = useCallback((next: KbLivePhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  // The session is live only once the customer is heard (see useFirstCustomerVoice).
  const voice = useFirstCustomerVoice({
    onVoice: () => {
      if (phaseRef.current === 'connecting') moveTo('live');
    },
    onTimeout: () => failRef.current(TRAINING_CALL_COPY.customerSilent),
  });

  const clearEndTimer = useCallback(() => {
    if (endTimerRef.current !== null) clearTimeout(endTimerRef.current);
    endTimerRef.current = null;
  }, []);

  const teardown = useCallback(async () => {
    voice.disarm();
    resetTurn();
    clearEndTimer();
    kbLiveWebSocketService.disconnect();
    await playbookLiveAudio.stop();
  }, [clearEndTimer, resetTurn, voice]);

  const fail = useCallback(
    (message: string) => {
      // Once complete, the result stands: a late close is not a failure.
      if (phaseRef.current === 'completed') return;
      console.warn('KbLive: session failed', { message });
      setError(message);
      setIsEnding(false);
      moveTo('preflight');
      void teardown();
    },
    [moveTo, teardown]
  );
  failRef.current = fail;

  const complete = useCallback(() => {
    setIsEnding(false);
    moveTo('completed');
    void teardown();
  }, [moveTo, teardown]);

  const start = useCallback(async () => {
    if (!trackId) {
      setError(KB_LIVE_COPY.noTrack);
      return;
    }
    setError(null);
    dispatch({ type: 'reset' });
    feed.resetFeed();
    resetTurn();
    moveTo('connecting');
    voice.arm();
    kbLiveWebSocketService.setHandlers(
      buildHandlers(dispatch, feed, voice, { onCompleted: complete, onFailed: fail, onCustomerAudio: noteCustomerAudio })
    );

    try {
      await playbookLiveAudio.start({
        onMicChunk: (pcm) => {
          kbLiveWebSocketService.sendAudio(pcm);
          noteMicAudio(pcm);
        },
      });
      // Still 'connecting' here: the phase flips to live on the customer's
      // first audible word, not when the socket opens.
      await kbLiveWebSocketService.connect(trackId, usableSessionId(sessionId));
    } catch (startError) {
      const message =
        startError instanceof Error && Object.values(KB_LIVE_ERRORS).includes(startError.message as never)
          ? startError.message
          : KB_LIVE_COPY.startFailed;
      fail(message);
    }
  }, [complete, fail, feed, moveTo, noteCustomerAudio, noteMicAudio, resetTurn, sessionId, trackId, voice]);

  const end = useCallback(() => {
    if (phaseRef.current !== 'live') return;
    setIsEnding(true);
    kbLiveWebSocketService.endSession();
    clearEndTimer();
    endTimerRef.current = setTimeout(() => fail(END_UNCONFIRMED_MESSAGE), END_CONFIRMATION_TIMEOUT_MS);
  }, [clearEndTimer, fail]);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => () => { void teardown(); }, [teardown]);

  return {
    phase,
    state,
    isEnding,
    turn,
    error,
    clearError,
    start,
    end,
    teardown,
  };
}
